import { CHALLENGE_IDS, type ChallengeId, FESTIVAL } from '../../data/full/festival';
import { isOneOf } from '../../shared/guards';
import type { GameContent } from '../../data/types';
import { t } from '../../shared/i18n';
import { SUN_PALETTES } from '../../shared/palette';
import { toEnd } from '../../sim/full/festival';
import { playedTempo, setOf } from '../../sim/lineup';
import { challengeOf } from '../../sim/full/festival-scale';
import type { FestivalChallenge, FestivalScene, FestivalState, SimState } from '../../sim/state';
import type { UiFrame } from '../types';
import { el, setText } from '../dom';
import { formatDuration, formatNumber } from '../format';
import type { HudParts, HudPart } from '../hud-parts';
import './festival.css';

// 1 to 4.
function stageNumber(festival: FestivalState): number {
  return festival.scenes.length;
}

export function stageOf(festival: FestivalState, total = FESTIVAL.setIds.length): string {
  return t('Scène {stage} sur {total}', { stage: stageNumber(festival), total });
}

export function gainLine(state: Pick<SimState, 'enemyScale'>): string {
  const scale = state.enemyScale;
  if (scale === undefined) {
    return '';
  }
  return t('{percent} % de vie et de coups en plus pour les bad vibes', {
    percent: formatNumber(Math.round((scale.hp - 1) * 100)),
  });
}

const FLIGHT_MS = 900;
const POP_MS = 360;

const CHALLENGE_NAMES: Readonly<Record<ChallengeId, () => string>> = {
  rapides: () => t('bad vibes plus rapides'),
  nombreuses: () => t('bad vibes plus nombreuses'),
  boss: () => t('boss plus coriace'),
};

// The challenges the team took on the way to this stage, and what they bring.
export function challengeLine(scene: FestivalScene | undefined): string {
  const challenges = scene?.challenges;
  if (challenges === undefined) {
    return '';
  }
  const names = challenges.ids.map((id) =>
    isOneOf(id, CHALLENGE_IDS) ? CHALLENGE_NAMES[id]() : id,
  );
  return t('Défis : {list}, {percent} % de score en plus', {
    list: names.join(', '),
    percent: formatNumber(Math.round((challenges.scoreMul - 1) * 100)),
  });
}

// The farthest player's way to its end, at their own pace; a player whose peer left is not waited for.
export function walkLeft(state: SimState): number {
  const side = state.festival?.taken?.side;
  if (side === undefined) {
    return 0;
  }
  return Math.max(
    0,
    ...state.players
      .filter((player) => player.gone !== true)
      .map(
        (player) =>
          Math.max(0, toEnd(player, state.arena, side) - FESTIVAL.path.endDepth) /
          Math.max(player.speed, 1),
      ),
  );
}

export function createFestivalHud({ ribbon, overlay }: HudParts): HudPart {
  const dots = el('div', 'ui-fest');
  dots.setAttribute('role', 'img');
  const points = FESTIVAL.setIds.map(() => dots.appendChild(el('span', 'ui-fest__dot')));
  const toward = el('div', 'ui-fest__toward');
  const towardName = el('span', 'ui-fest__name');
  const walk = el('span', 'ui-fest__walk');
  walk.setAttribute('role', 'timer');
  // What the boards taken on this path add to the score of the stage ahead.
  const bonus = el('span', 'ui-fest__bonus');
  bonus.hidden = true;
  toward.append(el('span', 'ui-fest__label', t('Vers')), towardName, walk, bonus);
  // The boards taken whose gain has flown off, and those whose gain has landed in the pill.
  let flown = new Set<number>();
  let landed = new Map<number, number>();

  const landedScore = (): number => [...landed.values()].reduce((sum, score) => sum + score, 0);

  function showBonus(): void {
    const score = landedScore();
    bonus.hidden = score === 0;
    setText(bonus, t('+{percent} %', { percent: formatNumber(Math.round(score * 100)) }));
  }

  function pop(scale: number): void {
    if (overlay.closest('.calm') === null) {
      bonus.animate(
        [
          { transform: 'scale(1)' },
          { transform: `scale(${String(scale)})`, boxShadow: '0 0 14px var(--or)', offset: 0.4 },
          { transform: 'scale(1)' },
        ],
        { duration: POP_MS, easing: 'ease-out' },
      );
    }
  }

  // A board dropped while its gain flies lands nothing.
  function land(index: number, score: number): void {
    if (!flown.has(index)) {
      return;
    }
    landed.set(index, score);
    showBonus();
    pop(1.35);
  }

  function drop(index: number): void {
    flown.delete(index);
    if (landed.delete(index)) {
      showBonus();
      pop(0.75);
    }
  }

  // The gain of a board taken leaves it for the pill of the banner, on a curve.
  function fly(board: FestivalChallenge, index: number, frame: UiFrame): void {
    const score = challengeOf(board.id).score;
    const start = frame.toScreen?.(board);
    if (start === undefined || overlay.closest('.calm') !== null) {
      land(index, score);
      return;
    }
    bonus.hidden = false;
    // The game's view lies under the interface's root, at the same place.
    const origin = (overlay.closest('.ui') ?? overlay).getBoundingClientRect();
    const target = bonus.getBoundingClientRect();
    bonus.hidden = landedScore() === 0;
    const gain = el(
      'span',
      'ui-fest__flight',
      t('+{percent} %', { percent: formatNumber(Math.round(score * 100)) }),
    );
    gain.style.left = `${String(origin.left + start.x)}px`;
    gain.style.top = `${String(origin.top + start.y)}px`;
    overlay.append(gain);
    const dx = target.left + target.width / 2 - (origin.left + start.x);
    const dy = target.top + target.height / 2 - (origin.top + start.y);
    const flight = gain.animate(
      [
        { transform: 'translate(-50%, -50%) scale(1.25)', opacity: 1 },
        {
          transform: `translate(calc(-50% + ${String(dx * 0.35)}px), calc(-50% + ${String(dy * 0.15)}px)) scale(1.15)`,
          opacity: 1,
          offset: 0.35,
        },
        {
          transform: `translate(calc(-50% + ${String(dx)}px), calc(-50% + ${String(dy)}px)) scale(0.8)`,
          opacity: 0.9,
        },
      ],
      { duration: FLIGHT_MS, easing: 'ease-in' },
    );
    void flight.finished
      .catch(() => undefined)
      .then(() => {
        gain.remove();
        land(index, score);
      });
  }
  ribbon.prepend(dots);
  ribbon.append(toward);

  const card = el('div', 'ui-fest__card');
  card.setAttribute('role', 'status');
  for (const token of ['sol', 'or', 'noyau', 'texte'] as const) {
    card.style.setProperty(`--${token}`, SUN_PALETTES.nuit[token]);
  }
  const kicker = el('span', 'ui-fest__kicker');
  const title = el('span', 'ui-fest__title');
  const line = el('span', 'ui-fest__line');
  card.append(kicker, title, line);
  card.hidden = true;
  overlay.append(card);

  return {
    update(state: SimState, content: GameContent, frame: UiFrame) {
      const { festival } = state;
      if (festival?.phase !== 'path') {
        flown = new Set();
        landed = new Map();
        bonus.hidden = true;
      }
      festival?.challenges?.forEach((board, index) => {
        if (board.takenTick !== undefined && !flown.has(index)) {
          flown.add(index);
          fly(board, index, frame);
        } else if (board.takenTick === undefined && flown.has(index)) {
          drop(index);
        }
      });
      dots.hidden = festival === undefined;
      toward.hidden = festival?.phase !== 'path';
      card.hidden = true;
      ribbon.toggleAttribute('data-path', festival?.phase === 'path');
      if (festival === undefined) {
        return;
      }
      const now = stageNumber(festival) - 1;
      const walking = festival.phase === 'path';
      points.forEach((point, index) => {
        const held = index < now || (index === now && (walking || festival.phase === 'sunrise'));
        point.dataset.state = held
          ? 'done'
          : index === now || (walking && index === now + 1)
            ? 'now'
            : 'next';
      });
      dots.setAttribute('aria-label', stageOf(festival));

      const taken = festival.taken;
      if (walking && taken !== undefined) {
        setText(towardName, t(setOf(content, taken.setId).name));
        setText(walk, t('{time} de marche', { time: formatDuration(walkLeft(state)) }));
        return;
      }
      const stage = setOf(content, state.setId);
      const scene = festival.scenes.at(-1);
      if (festival.phase === 'sunrise') {
        card.hidden = false;
        card.dataset.kind = 'sunrise';
        line.hidden = false;
        setText(kicker, `${t('Sunrise')} · ${stageOf(festival)}`);
        setText(title, t('{stage} a tenu', { stage: t(stage.name) }));
        setText(
          line,
          state.players.length > 1
            ? t('Regroupez-vous dans la même sortie pour choisir votre prochaine scène.')
            : t('Choisis une sortie sur la piste pour filer vers ta prochaine scène.'),
        );
        return;
      }
      const since = state.tick - (scene?.fromTick ?? Number.NEGATIVE_INFINITY);
      if (since >= 0 && since < playedTempo(stage, state).ticksPerBar) {
        card.hidden = false;
        card.dataset.kind = 'arrival';
        setText(kicker, stageOf(festival));
        setText(title, t(stage.name));
        setText(
          line,
          [gainLine(state), challengeLine(scene)].filter((part) => part !== '').join(' · '),
        );
        line.hidden = line.textContent === '';
      }
    },
  };
}
